import { useEffect, useRef, useState } from "react";
import type { ReportCategory } from "@fieldpath/shared";

import { api } from "../api/client";
import type { Fetcher } from "../api/client";

/**
 * "Report a problem", in the header, on every screen.
 *
 * **If reporting is hard, people stop — and the reports you lose are
 * disproportionately the small ones that turn out to be patterns.** So it is one
 * click from wherever they are, not a separate site, not an email, and it asks
 * for as little as possible: a category and what happened.
 *
 * Everything else is filled in by the app, because it already knows: which page
 * they were on, who they are, which build they were looking at, and the
 * reference from the last error they saw. Asking a scientist to copy a URL out
 * of the address bar is exactly the friction that loses the report.
 */
export function ReportProblem({ fetcher }: { fetcher: Fetcher }) {
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState<ReportCategory[] | null>(null);
  const [categoryId, setCategoryId] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  // Loaded when the panel first opens, not on every page load: this control
  // appears on every screen and must cost nothing until somebody wants it.
  useEffect(() => {
    if (!open || categories) return;
    void api
      .reportCategories(fetcher)
      .then((list) => {
        setCategories(list);
        setCategoryId(list[0]?.id ?? "");
        setError(null);
      })
      // A report needs a category -- `problem_reports.category_id` is NOT NULL
      // and the route requires it -- so Send stays off without the list. Until
      // 22 Oct this said "you can still describe the problem", which the
      // disabled button then refused. Closing and opening the panel retries.
      .catch(() =>
        setError("Could not load the list, and a report needs it. Close this and open it again, or reload the page."),
      );
  }, [open, categories, fetcher]);

  useEffect(() => {
    if (open) bodyRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  async function submit(): Promise<void> {
    if (!body.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      await api.reportProblem(fetcher, {
        categoryId,
        body,
        // The app knows where they are. Captured at SUBMIT rather than at open,
        // so it is the screen they were looking at when they decided to report.
        route: window.location.pathname + window.location.search,
        // The reference from the last 500 they saw, if any. This is the single
        // field that ties a report to a line in the log.
        reference: lastErrorReference(),
        appBuild: import.meta.env.VITE_BUILD_SHA ?? "dev",
      });
      setSent(true);
      setBody("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not send that. Try again?");
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <button className="report-open" onClick={() => setOpen(true)} title="Report a problem">
        Report a problem
      </button>
    );
  }

  return (
    <div className="report-panel" role="dialog" aria-label="Report a problem">
      {sent ? (
        <div className="report-done">
          <p>
            <strong>Thank you — that is logged.</strong>
          </p>
          {/* Deliberately no status, no ticket number, no "we will update you".
              Triage happens in Jeremy's punch list, outside the app, and
              promising a thread here would mean keeping two records in step.
              Jeremy reads these and closes the loop in person (Jeremy,
              23 Oct 2026). */}
          <p className="muted">
            Jeremy reads these. Jeremy will come back to you directly if more is
            needed.
          </p>
          <div className="row">
            <button
              onClick={() => {
                setSent(false);
                setOpen(false);
              }}
            >
              Close
            </button>
            <button className="linkish" onClick={() => setSent(false)}>
              Report something else
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="row spread">
            <strong>Report a problem</strong>
            <button className="linkish" onClick={() => setOpen(false)} aria-label="Close">
              ×
            </button>
          </div>

          <label className="field">
            <span>What kind of thing is it?</span>
            <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
              {(categories ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>What happened?</span>
            <textarea
              ref={bodyRef}
              rows={5}
              maxLength={4000}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="What you were doing, and what you expected instead."
            />
          </label>

          <p className="muted small">
            We will also record which page you are on and which version you are
            using, so you do not have to.
          </p>

          {error ? <p className="error">{error}</p> : null}

          <div className="row">
            <button
              className="primary"
              disabled={!body.trim() || !categoryId || saving}
              onClick={() => void submit()}
            >
              {saving ? "Sending…" : "Send"}
            </button>
            <button className="linkish" onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * The reference from the most recent 500, stashed by the API client.
 *
 * sessionStorage rather than React state because the error and the report
 * happen in different components and often minutes apart — somebody hits an
 * error, stares at it, then decides to tell us.
 */
function lastErrorReference(): string | null {
  try {
    return window.sessionStorage.getItem("fieldpath:lastErrorReference");
  } catch {
    // Private windows and blocked site data both throw here. A missing
    // reference is a slightly worse report, never a failed one.
    return null;
  }
}
